import { ApiFehler } from './api';
import type { GeraeteSpeicher } from './geraetespeicher';
import { pruefeKeinOffenerVorgang } from './vorgang';

/**
 * A5-2/A5-3 (Testserver 01.11.2026, dd7f868): Ablauf eines Offline-Abgleichs für den Kopf der
 * Kasse – beim Start, bei Wiederverbindung und im Intervall.
 *
 *  - Ohne Verbindung wird nichts übertragen, der Kopf zeigt aber den Puffer aus dem
 *    Gerätespeicher (vorher kehrte der Lauf ohne Zählung zurück: nach einem offline geschlossenen
 *    und offline wieder geöffneten Tab fehlte „n Beleg(e) offline gepuffert“).
 *  - Hat die Übertragung Belege nachsigniert, wird die Zahl der ungeklärten Verkäufe danach neu
 *    bestimmt (vorher nur davor – die Meldung „Verkauf noch ungeklärt“ blieb stehen).
 */
export interface AbgleichSchritte {
  online: () => boolean;
  /** Wiederaufnahme offener Vorgänge am Server; liefert die Zahl der noch ungeklärten. */
  wiederaufnahme: () => Promise<number>;
  /** Offline-Puffer übertragen; liefert die Zahl nachsignierter Belege. */
  uebertrage: () => Promise<number>;
  /** Gepufferte Belege im Gerätespeicher zählen und anzeigen. */
  zaehlePuffer: () => Promise<void>;
  setzeUngeklaert: (anzahl: number) => void;
  /**
   * E-R12-INT P4 (sunmi1-Befund): Grund einer Ablehnung durch den Sicherheitsvertrag (App zu
   * alt, Freigabe nötig, Kasse gesperrt) in der vorhandenen Abgleichmeldung zeigen – sonst sähe
   * der Bediener nur die Zahl der Offline-Belege. Der Puffer bleibt unverändert.
   */
  meldeGrund?: (text: string) => void;
}

/** Ablehnungen nach R-12 (Server `signatursperre.ts`/`zeugenfreigabe.ts`), kein Netzfehler. */
const R12_CODES: ReadonlySet<string> = new Set([
  'app_update_erforderlich',
  'zeugenfreigabe_erforderlich',
  'kasse_vorlaeufig_gesperrt',
  'kasse_signatur_gesperrt',
  'geraetezeuge_unbekannt',
  'geraetezeuge_unbestaetigt',
]);

/** Text für die Abgleichmeldung, wenn der Abgleich aus einem R-12-Grund abgelehnt wurde. */
export function r12Grund(e: unknown): string | null {
  if (!(e instanceof ApiFehler) || e.daten === null) {
    return null;
  }
  const code = e.daten.code;
  return (typeof code === 'string' && R12_CODES.has(code)) ||
    e.daten.geraetespeicherNichtLesbar === true
    ? `Offline-Belege nicht übertragen: ${e.message}`
    : null;
}

export interface AbgleichErgebnis {
  /** Lief der Abgleich mit Verbindung (sonst nur gezählt)? */
  online: boolean;
  nachsigniert: number;
  /** Zuletzt bestimmte Zahl ungeklärter Verkäufe; null = nicht bestimmt (Fehler, offline). */
  ungeklaert: number | null;
}

export async function abgleichLauf(s: AbgleichSchritte): Promise<AbgleichErgebnis> {
  if (!s.online()) {
    await s.zaehlePuffer().catch(() => undefined);
    return { online: false, nachsigniert: 0, ungeklaert: null };
  }
  let nachsigniert = 0;
  let ungeklaert: number | null = null;
  try {
    // Ein Fehler der Wiederaufnahme lässt die bisherige Anzeige stehen (im Zweifel warnen)
    ungeklaert = await s.wiederaufnahme().catch(() => null);
    if (ungeklaert !== null) {
      s.setzeUngeklaert(ungeklaert);
    }
    nachsigniert = await s.uebertrage();
    if (nachsigniert > 0) {
      const danach = await s.wiederaufnahme().catch(() => null);
      if (danach !== null) {
        ungeklaert = danach;
        s.setzeUngeklaert(danach);
      }
    }
  } catch (e: unknown) {
    // Kasse weiterhin nicht erreichbar – nächster Versuch beim nächsten Intervall. Eine
    // Ablehnung nach R-12 bekommt ihren Grund angezeigt (der Puffer bleibt).
    const grund = r12Grund(e);
    if (grund !== null) {
      s.meldeGrund?.(grund);
    }
  }
  await s.zaehlePuffer().catch(() => undefined);
  return { online: true, nachsigniert, ungeklaert };
}

/** Text einer Meldung „… noch ungeklärt …“ (Verkauf, Kartenzahlung, Lieferdienst-Erfassung)? */
export function istUngeklaertMeldung(text: string | null): boolean {
  return text?.includes('noch ungeklärt') === true;
}

/**
 * A5-3: Ist der Grund einer angezeigten „noch ungeklärt“-Meldung erledigt – kein offener
 * Vorgangsdatensatz und keine Kassierreservierung dieser Kasse mehr? Ein Lesefehler gilt als
 * nicht erledigt (die Meldung bleibt).
 */
export async function ungeklaertErledigt(
  speicher: GeraeteSpeicher,
  kassenId: string,
): Promise<boolean> {
  try {
    await pruefeKeinOffenerVorgang(speicher, kassenId);
    return true;
  } catch {
    return false;
  }
}
